'use client';

import React from 'react';
import Image from 'next/image';
import ServiceCarousel from '../../common/ServiceCarousel';
import { useContactModal } from '../../../contexts/ContactModalContext';

/*
  Brand palette
  #080400  Pitch Black
  #00145B  Deep Twilight
  #0047AB  Cobalt Blue
  #3730A3  True Azure (purple)
  #CEFF1A  Chartreuse
  #FBFDFD  White
*/

export default function SpaceHero() {
    const { openModal } = useContactModal();
    return (
        <>
            <style>{`
                @keyframes sh-entry-up {
                    from { opacity:0; transform:translateY(28px); }
                    to   { opacity:1; transform:translateY(0); }
                }
                @keyframes sh-entry-down {
                    from { opacity:0; transform:translateY(-20px); }
                    to   { opacity:1; transform:translateY(0); }
                }
                @keyframes sh-hex-pulse { 0%,100%{opacity:.03} 50%{opacity:.07} }
                @keyframes sh-scan {
                    0%   { transform:translateY(-100%); opacity:0; }
                    5%   { opacity:.7; }
                    95%  { opacity:.7; }
                    100% { transform:translateY(110vh); opacity:0; }
                }
                .sh-h1    { animation: sh-entry-up   .80s .22s ease both; }
                .sh-ctas  { animation: sh-entry-down .70s .55s ease both; }

                .sh-btn-primary:hover {
                    transform: translateY(-2px);
                }
                .sh-btn-primary {
                    transition: box-shadow .22s, background .22s, border-color .22s, transform .22s;
                }

                @media(max-width:640px){
                    .sh-ctas { flex-direction:column !important; align-items:center !important; }
                }

                .sh-hero { min-height: clamp(520px, 80vh, 100vh); }
                .sh-hero-video { display: none; }
                @media(min-width:768px){
                    .sh-hero-video { display: block; }
                }
                @media(min-width:1024px){
                    .sh-hero { min-height: 94vh; }
                }
                @media(min-width:1440px){
                    .sh-hero { min-height: 100vh; }
                }
            `}</style>

            <section className="sh-hero" style={{
                position: 'relative',
                width: '100%',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
                background: '#080400',
            }}>
                {/* ── Background image: the LCP element on mobile. A real <img> (not a video poster)
                     so it's preloaded with high priority and served at a phone-sized width. ── */}
                <Image
                    src="/images/hero-bg.webp"
                    alt=""
                    fill
                    priority
                    fetchPriority="high"
                    sizes="100vw"
                    quality={70}
                    style={{ objectFit: 'cover', objectPosition: 'center top', zIndex: 0 }}
                />

                {/* ── Background video (desktop only; plays over the image once loaded) ── */}
                {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
                <video
                    className="sh-hero-video"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="none"
                    aria-hidden="true"
                    style={{
                        position: 'absolute',
                        inset: 0,
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        objectPosition: 'center top',
                        zIndex: 0,
                    }}
                >
                    <source src="/videos/hero-bg.mp4" type="video/mp4" media="(min-width: 768px)" />
                </video>

                {/* ── Hex grid overlay ── */}
                <div style={{
                    position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none',
                    backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='52' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M30 2l28 16v16L30 50 2 34V18z' fill='none' stroke='rgba(0,71,171,0.07)' stroke-width='0.6'/%3E%3C/svg%3E")`,
                    backgroundSize: '60px 52px',
                    animation: 'sh-hex-pulse 9s ease-in-out infinite',
                }} />

                {/* ── Scan line ── */}
                <div style={{ position: 'absolute', inset: 0, zIndex: 3, pointerEvents: 'none', overflow: 'hidden' }}>
                    <div style={{
                        position: 'absolute', left: 0, right: 0, height: 1,
                        background: 'linear-gradient(90deg, transparent 0%, rgba(55,48,163,.25) 30%, rgba(0,71,171,.45) 50%, rgba(55,48,163,.25) 70%, transparent 100%)',
                        animation: 'sh-scan 14s cubic-bezier(.4,0,.6,1) infinite',
                        animationDelay: '2s',
                    }} />
                </div>

                {/* ── Dark overlay — improve text legibility ── */}
                <div style={{
                    position: 'absolute', inset: 0, zIndex: 4, pointerEvents: 'none',
                    background: 'rgba(4,2,0,0.38)',
                }} />

                {/* ── Top fade — smooth into navbar ── */}
                <div style={{
                    position: 'absolute', top: 0, left: 0, right: 0, height: 160,
                    zIndex: 5, pointerEvents: 'none',
                    background: 'linear-gradient(to bottom, #080400 0%, rgba(8,4,0,0.2) 60%, transparent 100%)',
                }} />

                {/* ── Bottom fade — smooth into next section ── */}
                <div style={{
                    position: 'absolute', bottom: 0, left: 0, right: 0, height: 220,
                    zIndex: 5, pointerEvents: 'none',
                    background: 'linear-gradient(to top, #080400 0%, rgba(8,4,0,0.5) 40%, transparent 100%)',
                }} />

                {/* ── Left / Right vignette ── */}
                <div style={{
                    position: 'absolute', inset: 0, zIndex: 5, pointerEvents: 'none',
                    background: 'radial-gradient(ellipse at center, transparent 55%, rgba(4,2,0,0.55) 100%)',
                }} />

                {/* ════════════════════════════════════════
                    MAIN CONTENT
                ════════════════════════════════════════ */}
                <div style={{
                    position: 'relative', zIndex: 10,
                    flex: 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    paddingTop: 'clamp(60px, 8vh, 130px)',
                    paddingBottom: 'clamp(30px, 5vh, 100px)',
                    paddingLeft: 24,
                    paddingRight: 24,
                    marginTop: 'clamp(60px, 14vh, 200px)',
                }}>

                    {/* Headline */}
                    <h1 className="sh-h1" style={{
                        fontFamily: 'var(--font-clash), "Clash Display", sans-serif',
                        fontWeight: 900,
                        lineHeight: 0.98,
                        letterSpacing: '0.04em',
                        color: '#FBFDFD',
                        margin: '0 0 clamp(32px, 4.5vh, 52px) 0',
                        textShadow: '0 2px 40px rgba(0,0,0,0.5)',
                    }}>
                        <span style={{
                            display: 'block',
                            fontSize: 'clamp(16px, 1.8vw, 26px)',
                            fontWeight: 700,
                            color: '#B9B9B2',
                            wordSpacing: '0.12em',
                        }}>
                            Digital marketing that compounds your
                        </span>
                        {' '}
                        <span style={{
                            display: 'block',
                            fontSize: 'clamp(48px, 7vw, 96px)',
                            color: '#CEFF1A',
                        }}>
                            Growth
                        </span>
                    </h1>

                    {/* CTA buttons */}
                    <div className="sh-ctas" style={{ display: 'flex', gap: 14, flexWrap: 'wrap', justifyContent: 'center' }}>
                        <button
                            onClick={openModal}
                            className="sh-btn-primary"
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 10,
                                padding: '14px 32px', borderRadius: 999, textDecoration: 'none', cursor: 'pointer',
                                background: 'rgba(255,255,255,0.1)',
                                border: '1px solid rgba(255,255,255,0.25)',
                                backdropFilter: 'blur(16px)',
                                WebkitBackdropFilter: 'blur(16px)',
                                boxShadow: '0 4px 24px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.2)',
                                fontFamily: 'var(--font-manrope, Manrope, sans-serif)',
                                fontWeight: 700, fontSize: 15, letterSpacing: '.04em', color: '#FBFDFD',
                            }}
                        >
                            Start Your Project
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                                <path d="M3 8h10M9 4l4 4-4 4" stroke="#FBFDFD" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                            </svg>
                        </button>
                    </div>

                </div>

                {/* ── Service ticker at bottom ── */}
                <div style={{ position: 'relative', zIndex: 10, paddingBottom: 16 }}>
                    <ServiceCarousel />
                </div>
            </section>
        </>
    );
}
